import { Plus } from "@phosphor-icons/react"
import { Button, ButtonProps, Spinner, Toast } from "@vesatogo/grass-core"
import axios from "axios"
import React, { RefAttributes, useState } from "react"
import toast from "react-hot-toast"
import { v4 as uuid4 } from "uuid"

export type FileUploadButtonProps = {
  endpoint: string
  onChange?: (newArray: any[]) => void
  onError: (message: string) => void
  onSuccess?: (message: any) => void
  onFileSelect?: (files: FileList | null) => void | Promise<boolean>
  maxFiles?: number
  maxFileSize?: number // in bytes
  multiple?: boolean
  access?: "public-read" | "authenticated-read" | "private"
  shouldEncrypt?: boolean
  className?: string
  disabled?: boolean
  method?: "post" | "put"
  uploadCardClassName?: string
  "data-testid"?: string
  environment?: string
  appName?: string
  media?: any[]
  accept?: string
  type?: "button" | "upload"
  buttonLabel?: string
  buttonIcon?: React.ReactElement
  buttonProps?: Omit<ButtonProps, "ref"> & RefAttributes<HTMLButtonElement>
}

function generateFilename(file: File) {
  const extension = file.type.split("/")[1]
  return `${uuid4()}.${extension}`
}

const FileUploadButton = ({
  endpoint,
  onChange,
  onError,
  onSuccess,
  onFileSelect,
  maxFiles = 8,
  maxFileSize = 10 * 1024 * 1024, // 10MB default
  multiple = true,
  access = "public-read",
  shouldEncrypt = false,
  className,
  disabled = false,
  method = "post",
  environment = "",
  appName = "dashboard",
  media = [],
  accept = "image/*",
  type = "button",
  buttonLabel = "Add Attachment",
  buttonIcon = <Plus size={16} />,
  buttonProps,
  ...props
}: FileUploadButtonProps) => {
  const fileInputRef = React.useRef<HTMLInputElement>(null)
  const [loading, setLoading] = useState(false)

  const uploadFiles = async (files: File[]) => {
    try {
      setLoading(true)
      if (!files?.length) return

      const path = [appName, environment].filter(str => str).join("/") + "/"
      const filesPromises = files.map(file => {
        const formData = new FormData()
        formData.append("file", file)
        formData.append("path", path)
        formData.append("access", access)
        formData.append("should_encrypt", shouldEncrypt.toString())
        formData.append("name", generateFilename(file))

        return method === "post"
          ? axios.post(endpoint, formData, {
              headers: {
                authorization: `Bearer ${localStorage.getItem("token")}`,
              },
            })
          : axios.put(endpoint, formData, {
              headers: {
                authorization: `Bearer ${localStorage.getItem("token")}`,
              },
            })
      })

      const uploadedFiles = await Promise.all(filesPromises)
      const newFiles = uploadedFiles.map(({ data }) => {
        const type = /(.jpeg|.jpg|.png|.webp)$/i.test(data.media.url)
          ? "image"
          : "file"

        return {
          url: data.media.url,
          is_encrypted: shouldEncrypt,
          media_kind: type,
          access,
        }
      })

      onChange?.(newFiles)
      onSuccess?.(uploadedFiles)
    } catch (err: any) {
      onError(`Something went wrong! ${err.message || err}`)
    } finally {
      setLoading(false)
    }
  }

  const handleFileChange = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const files = Array.from(event.target.files || [])
    const currentFileCount = media.length

    // Notify parent component of file selection and check if upload should proceed
    if (onFileSelect) {
      const shouldProceed = await onFileSelect(event.target.files)
      // If onFileSelect returns false, block the upload
      if (shouldProceed === false) {
        // Clear the file input so user can select the same file again
        if (event.target) {
          event.target.value = ""
        }
        return
      }
    }

    if (files.length + currentFileCount > maxFiles) {
      onError(`Cannot upload more than ${maxFiles} files`)
      return
    }

    // Validate file sizes
    const oversizedFiles = files.filter(file => file.size > maxFileSize)
    if (oversizedFiles.length > 0) {
      const maxSizeMB = (maxFileSize / (1024 * 1024)).toFixed(0)
      const fileNames = oversizedFiles.map(f => f.name).join(", ")
      onError(`File(s) exceed ${maxSizeMB}MB limit: ${fileNames}`)

      return toast.custom(
        <Toast
          intent="danger"
          title={`File(s) exceed ${maxSizeMB}MB limit: ${fileNames}`}
        />,
        {
          duration: 5000,
        }
      )
    }

    uploadFiles(files)
  }

  return (
    <>
      <input
        type="file"
        ref={fileInputRef}
        style={{ display: "none" }}
        onChange={handleFileChange}
        multiple={multiple}
        disabled={disabled}
        aria-label="Upload Files"
        accept={accept}
        {...props}
      />
      {type === "upload" ? (
        <div
          className="flex flex-col items-center justify-center my-auto w-[133px] h-[128px] rounded-lg hover:border-green-500 transition-colors cursor-pointer relative group border-dashed border-1 border-gray-400"
          onClick={() => fileInputRef.current!.click()}
        >
          <>
            {loading ? (
              <Spinner size={24} />
            ) : (
              <>
                <Plus className="!justify-self-center" size={20} />
                <div className="text-gray-700 mt-1 font-medium">Upload</div>
              </>
            )}
          </>
        </div>
      ) : (
        <Button
          className={className}
          intent="primary"
          variant="outline"
          onClick={() => fileInputRef.current!.click()}
          leftIcon={buttonIcon}
          disabled={disabled}
          {...buttonProps}
        >
          {loading ? <Spinner size={16} /> : buttonLabel}
        </Button>
      )}
    </>
  )
}

export default FileUploadButton
